import { Card, Input, Button, Select, Form, Avatar } from 'antd';
import { StarFilled, CheckCircleFilled, TrophyFilled, HeartFilled } from '@ant-design/icons';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import './BusinessDetailsStep.scss';
import { RegistrationData } from '../UserRegistrationPage';
import posthog from 'posthog-js';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAllIntegrations } from '@/api/integration';
import MikeChenProfile from '/mike-chen.png';
import { getResellerData } from '@/store/reseller/selector';
import { safeJSONParse } from '@/utils/helper';

interface SimpleIntegration {
	name: string;
	businessType: string;
}

interface BusinessDetailsStepProps {
	debrand?: boolean;
	resellerLogoUrl?: string;
	registrationData: RegistrationData;
	setRegistrationData: (data: RegistrationData) => void;
	onSubmit: (data: Partial<RegistrationData>) => void;
	resellerBusinessName?: string;
}

const businessTypes = [
	{
		id: 'hvac',
		label: 'Field Service Contractors (HVAC/Plumbing/Electrical, etc.)',
		value: 'HVAC',
	},
	{ id: 'auto-sales', label: 'Auto Sales & Service' },
	{ id: 'auto-repair', label: 'Auto Repair' },
	{ id: 'car-dealership', label: 'Car Dealership' },
	{ id: 'other-automotive', label: 'Other Automotive Business' },
	{ id: 'beauty', label: 'Beauty' },
	{ id: 'construction', label: 'Construction' },
	{ id: 'retail', label: 'Retail' },
	{ id: 'food', label: 'Food' },
	{ id: 'legal', label: 'Legal' },
	{ id: 'marketing', label: 'Marketing' },
	{ id: 'technology', label: 'Technology' },
	{ id: 'entertainment', label: 'Entertainment' },
	{ id: 'real-estate', label: 'Real Estate' },
	{ id: 'other', label: 'Other' },
];

const testimonials = [
	{
		id: 1,
		name: 'Sarah Johnson',
		business: "Johnson's Auto Repair",
		avatar:
			'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=64&h=64&fit=crop&crop=face',
		content:
			'Our AI receptionist has been a game-changer. We used to miss calls when we were under the hood, but now every customer gets immediate attention. Bookings are up 40%!',
		rating: 5,
	},
	{
		id: 2,
		name: 'Mike Chen',
		business: 'Smile Dental',
		avatar: MikeChenProfile,
		content:
			"The appointment scheduling feature is incredible. Patients can book 24/7, and the AI handles rescheduling perfectly. It's like having a full-time receptionist at a fraction of the cost.",
		rating: 5,
	},
	{
		id: 3,
		name: 'Lisa Rodriguez',
		business: 'Bella Beauty Salon',
		avatar:
			'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=64&h=64&fit=crop&crop=face',
		content:
			"I was skeptical at first, but clients love how professional and quick the responses are. The AI perfectly answers all my customer's questions and allows me to focus on running my salon!",
		rating: 5,
	},
];

const BusinessDetailsStep = ({
	debrand = false,
	resellerLogoUrl,
	registrationData,
	setRegistrationData,
	onSubmit,
	resellerBusinessName,
}: BusinessDetailsStepProps) => {
	const [form] = Form.useForm();
	const [selectedBusinessType, setSelectedBusinessType] = useState<string>(
		registrationData.businessType || '',
	);
	const [matchingIntegration, setMatchingIntegration] = useState<SimpleIntegration | null>(null);
	const currentReseller = useAppSelector(getResellerData);

	const { data: fetchedIntegrations } = useQuery({
		queryKey: ['integrations'],
		queryFn: getAllIntegrations,
	});

	const integrations = fetchedIntegrations
		? [
				...fetchedIntegrations,
				{
					name: 'GoHighLevel',
					businessType: JSON.stringify(['Marketing']),
				},
			]
		: [];

	useEffect(() => {
		if (selectedBusinessType && integrations.length > 0) {
			findMatchingIntegration(selectedBusinessType);
		}
	}, [selectedBusinessType, integrations]);

	useEffect(() => {
		if (registrationData.websiteUrl) {
			let displayUrl = registrationData.websiteUrl;
			if (displayUrl.startsWith('https://')) {
				displayUrl = displayUrl.substring(8);
			} else if (displayUrl.startsWith('http://')) {
				displayUrl = displayUrl.substring(7);
			}
			form.setFieldsValue({ websiteUrl: displayUrl });
		}
	}, [registrationData.websiteUrl, form]);

	const findMatchingIntegration = (businessType: string, integrationsData = integrations) => {
		if (!businessType || integrationsData.length === 0) {
			setMatchingIntegration(null);
			return;
		}

		const match =
			integrationsData.find((integration) => {
				try {
					const supportedBusinessTypes = safeJSONParse(integration?.businessType, []);
					return supportedBusinessTypes.includes(businessType);
				} catch (e) {
					console.error('Error parsing business types:', e);
					return false;
				}
			}) || null;

		setMatchingIntegration(match);
	};

	const handleBusinessTypeChange = (value: string) => {
		setSelectedBusinessType(value);
		findMatchingIntegration(value);
	};

	const handleBusinessInfoSubmit = (values: Partial<RegistrationData>) => {
		posthog.capture('submitted-business-info-registration-v5');

		// Determine hasWebsite based on websiteUrl value
		const hasWebsiteValue = values.websiteUrl && values.websiteUrl.trim() ? 'yes' : 'no';

		const finalWebsiteUrl =
			values.websiteUrl && values.websiteUrl.trim()
				? `https://${values.websiteUrl.trim()}`
				: values.websiteUrl;

		const updatedData = {
			...registrationData,
			...values,
			websiteUrl: finalWebsiteUrl,
			hasWebsite: hasWebsiteValue,
		};
		setRegistrationData(updatedData);
		onSubmit(updatedData);
	};

	const isOtherBusinessType = Form.useWatch('businessType', form) === 'Other';

	return (
		<div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 business-details-step-v2 relative overflow-hidden">
			{/* Floating Logo with Enhanced Design */}
			{(!debrand || (debrand && resellerLogoUrl)) && (
				<div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-20">
					<div className="bg-white rounded-xl p-2 shadow-lg border border-white/20 backdrop-blur-sm">
						<div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-200 to-indigo-200 flex items-center justify-center">
							<img
								src={debrand ? resellerLogoUrl : 'new_logo.png'}
								alt=""
								className={`rounded-lg ${debrand ? 'w-12 h-12' : 'w-6 h-6'}`}
							/>
						</div>
					</div>
				</div>
			)}

			{/* Background Decorative Elements */}
			<div className="absolute inset-0 overflow-hidden pointer-events-none">
				<div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-indigo-500/20 rounded-full blur-3xl" />
				<div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-purple-400/20 to-pink-500/20 rounded-full blur-3xl" />
			</div>

			<div className="min-h-screen flex w-full relative z-10">
				{/* Left Section - Compact Testimonials */}
				<div className="hidden lg:flex lg:w-1/2 flex-col justify-center relative">
					<div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-900" />
					<div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />

					<div className="relative z-10 max-w-lg mx-auto p-6">
						{/* Compact Header */}
						<div className="mb-8 text-center">
							<div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-3 py-1 mb-4">
								<TrophyFilled className="text-yellow-400 text-sm" />
								<span className="text-white text-xs font-medium">Trusted by 1,000+ businesses</span>
							</div>
							<h2 className="text-3xl font-bold text-white mb-3 leading-tight">
								<span className="bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
									Double your revenue
								</span>{' '}
								with {debrand ? currentReseller?.businessName || resellerBusinessName : 'Frontdesk'}
								.
							</h2>
						</div>

						{/* Compact Testimonials */}
						{!debrand && (
							<div className="space-y-4">
								{testimonials.map((testimonial, index) => (
									<div
										key={testimonial.id}
										className="group bg-white/10 backdrop-blur-lg rounded-xl p-4 border border-white/20 hover:bg-white/15 hover:scale-105 transition-all duration-300 transform"
										style={{ animationDelay: `${index * 0.1}s` }}
									>
										<div className="flex mb-2">
											{[...Array(testimonial.rating)].map((_, i) => (
												<StarFilled key={i} className="text-yellow-400 text-sm" />
											))}
										</div>
										<blockquote className="text-blue-50 text-xs leading-relaxed mb-3 italic">
											"{testimonial.content}"
										</blockquote>
										<div className="flex items-center gap-3">
											<div className="relative">
												<Avatar
													src={testimonial.avatar}
													size={36}
													className="border-2 border-white/30"
												/>
											</div>
											<div className="flex flex-col">
												<h4 className="font-semibold text-white text-xs m-0">{testimonial.name}</h4>
												<p className="text-blue-200 text-xs m-0 flex items-center gap-1">
													<HeartFilled className="text-red-400 text-xs" />
													{testimonial.business}
												</p>
											</div>
										</div>
									</div>
								))}
							</div>
						)}
					</div>
				</div>

				{/* Right Section - Compact Form */}
				<div className="w-full lg:w-1/2 flex items-center justify-center p-4">
					<div className="w-full max-w-md">
						{/* Compact Header */}
						<div className="text-center mb-6">
							<h1 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-2">
								Start for Free Today
							</h1>
							<p className="text-gray-600 text-sm mb-3">
								Get your AI receptionist up and running in just 5 minutes
							</p>

							{/* Mobile Trust Badge - Only visible on mobile */}
							<div className="lg:hidden flex justify-center">
								<div className="inline-flex items-center gap-2 bg-blue-500 backdrop-blur-sm rounded-full px-4 py-2">
									<TrophyFilled className="text-yellow-400 text-sm" />
									<span className="text-white text-xs font-medium">
										Trusted by 1,000+ businesses
									</span>
								</div>
							</div>
						</div>

						{/* Compact Card */}
						<Card className="shadow-2xl rounded-3xl border-0 bg-white/80 backdrop-blur-sm overflow-hidden">
							<div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-indigo-500" />

							<div className="p-6">
								<Form
									form={form}
									layout="vertical"
									onFinish={handleBusinessInfoSubmit}
									requiredMark={false}
									className="space-y-4"
									initialValues={{
										...registrationData,
										websiteUrl: registrationData.websiteUrl
											? registrationData.websiteUrl.replace(/^https?:\/\//, '')
											: registrationData.websiteUrl,
									}}
								>
									{/* Business Name - Compact */}
									<Form.Item
										name="businessName"
										label={
											<span className="text-gray-700 font-semibold flex items-center gap-2 text-sm">
												Business Name *
											</span>
										}
										rules={[{ required: true, message: 'Please enter your business name' }]}
										className="mb-3"
									>
										<Input
											placeholder="Enter your business name"
											className="h-12 rounded-xl border-2 border-gray-200 hover:border-blue-400 focus:border-blue-500 transition-all duration-200 bg-gray-50/50"
											size="large"
										/>
									</Form.Item>

									{/* Business Type - Compact */}
									<Form.Item
										name="businessType"
										label={
											<span className="text-gray-700 font-semibold flex items-center gap-2 text-sm">
												Business Type *
											</span>
										}
										rules={[{ required: true, message: 'Please select your business type' }]}
										className="mb-3"
									>
										<Select
											options={businessTypes.map((type) => ({
												value: type.value || type.label,
												label: type.label,
											}))}
											placeholder="e.g., Restaurant, Dental Office, Auto Repair"
											size="large"
											className="rounded-xl business-type-select"
											onChange={handleBusinessTypeChange}
											style={{ height: '48px' }}
										/>
									</Form.Item>

									{/* Compact Integration Message */}
									{matchingIntegration && (
										<div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-3 animate-pulse mb-3">
											<div className="flex items-center text-green-700">
												<div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-2">
													<StarFilled className="text-green-600 text-xs" />
												</div>
												<div>
													<span className="font-semibold text-xs block">Amazing!</span>
													<span className="text-xs">
														We integrate perfectly with {matchingIntegration.name}
													</span>
												</div>
											</div>
										</div>
									)}

									{/* Custom Business Type - Compact */}
									{isOtherBusinessType && (
										<Form.Item
											name="otherBusinessType"
											label={
												<span className="text-gray-700 font-semibold flex items-center gap-2 text-sm">
													Please specify
												</span>
											}
											rules={[{ required: true, message: 'Please specify your business type' }]}
											className="mb-3"
										>
											<Input
												placeholder="e.g. Pet Grooming"
												className="h-12 rounded-xl border-2 border-gray-200 hover:border-purple-400 focus:border-purple-500 transition-all duration-200 bg-gray-50/50"
												size="large"
											/>
										</Form.Item>
									)}

									{/* Website URL - Compact */}
									<Form.Item
										name="websiteUrl"
										label={
											<span className="text-gray-700 font-semibold flex items-center gap-2 text-sm">
												Website
											</span>
										}
										rules={[
											{
												validator: (_, value) => {
													if (!value || !value.trim()) {
														return Promise.resolve();
													}

													const fullUrl = `https://${value.trim()}`;
													const urlPattern =
														/^https?:\/\/[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\/?$/;
													if (!urlPattern.test(fullUrl)) {
														return Promise.reject(
															new Error('Please enter a valid domain (e.g., example.com)'),
														);
													}
													return Promise.resolve();
												},
											},
										]}
										className="mb-4"
									>
										<Input
											addonBefore={<span className="text-gray-600 font-medium">https://</span>}
											placeholder="example.com"
											className="rounded-xl border-none bg-gray-50/50"
											size="large"
											onChange={(e) => {
												let value = e.target.value;

												if (value.startsWith('https://')) {
													value = value.substring(8);
												} else if (value.startsWith('http://')) {
													value = value.substring(7);
												}

												form.setFieldsValue({ websiteUrl: value });
											}}
										/>
									</Form.Item>

									{/* Compact Submit Button */}
									<Form.Item className="mb-0">
										<Button
											type="primary"
											htmlType="submit"
											className="w-full h-12 rounded-xl font-bold text-base bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 border-0 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
										>
											<span className="flex items-center justify-center gap-2">
												🚀 Build My Receptionist
											</span>
										</Button>
									</Form.Item>
								</Form>

								{/* Compact Trust Indicators - 2 Rows */}
								<div className="mt-4 pt-4 border-t border-gray-100">
									<div className="space-y-2 text-xs text-gray-500">
										{/* First Row */}
										<div className="flex items-center justify-center gap-4">
											<div className="flex items-center gap-1">
												<CheckCircleFilled className="text-green-500 text-xs" />
												<span>No credit card required</span>
											</div>
											<div className="flex items-center gap-1">
												<CheckCircleFilled className="text-green-500 text-xs" />
												<span>Setup in 5 minutes</span>
											</div>
										</div>
										{/* Second Row */}
										<div className="flex items-center justify-center gap-4">
											<div className="flex items-center gap-1">
												<CheckCircleFilled className="text-green-500 text-xs" />
												<span>Keep your existing phone number</span>
											</div>
										</div>
									</div>
								</div>
							</div>
						</Card>
					</div>
				</div>
			</div>
		</div>
	);
};

export default BusinessDetailsStep;
